import {
  RANKS,
  SUITS,
  byRankAsc,
  cardOf,
  cardsOfSuit,
  groupBySuit,
  rankOf,
  suitOf,
  type CardId,
  type Rank,
  type Suit,
} from './cards.js';
import { KITTY_SIZE, type Seat, type TrickPlay } from './types.js';
import type { PlayerView } from './view.js';

/**
 * Kural tabanlı bot.
 *
 * ÖNEMLİ: Bot girdi olarak `Match` değil `PlayerView` alıyor — yani bir insan
 * oyuncunun gördüğünden fazlasını göremiyor. Bu sayede (a) hile yapması
 * yapısal olarak imkânsız, (b) aynı bot istemcide offline modda da çalışıyor.
 *
 * TEMEL GÖZLEM: bu oyunda fazla el almanın CEZASI YOK. Puan "alınan − hedef"
 * olduğu için 8'ci de 3'cü de her turda daha fazla el almak istiyor; hedef
 * yalnızca sıfır çizgisini kaydırıyor. Bu yüzden bot bir ihale oyununda
 * olduğu gibi "tam tutturma" hesabı yapmıyor: her zaman en çok eli almaya
 * çalışıyor, sadece bunu ucuza yapmaya bakıyor.
 */

export type Difficulty = 'easy' | 'normal';

/* ------------------------------------------------------------------ *
 * Tur okuma
 * ------------------------------------------------------------------ */

/** Turu şu an tutan kart. */
function leadingCard(plays: readonly TrickPlay[], trump: Suit): CardId {
  const ledSuit = suitOf(plays[0]!.card);
  const trumped = plays.filter((p) => suitOf(p.card) === trump);
  const pool = trumped.length > 0 ? trumped : plays.filter((p) => suitOf(p.card) === ledSuit);
  return pool.reduce((a, b) => (rankOf(a.card) >= rankOf(b.card) ? a : b)).card;
}

/** Bu kart ŞU AN atılsa turu alır mı? (Arkadan gelenler yine geçebilir.) */
function wouldWin(card: CardId, plays: readonly TrickPlay[], trump: Suit): boolean {
  if (plays.length === 0) return true;
  const best = leadingCard(plays, trump);
  const ledSuit = suitOf(plays[0]!.card);

  if (suitOf(card) === trump) {
    return suitOf(best) !== trump || rankOf(card) > rankOf(best);
  }
  if (suitOf(best) === trump) return false;
  return suitOf(card) === ledSuit && rankOf(card) > rankOf(best);
}

/**
 * Bu kart, kendi renginin GÖRÜNMEYEN en büyüğü mü — yani lider konumunda
 * atılırsa o renkten kesin alır mı?
 *
 * Görülmüş sayılanlar: kendi elim, oynanmış kartlar ve (8'ciysem) gömdüğüm
 * kartlar. Yerdeki 4 kart 8'ci dışında kimseye görünmediği için diğer
 * oyuncular için burada küçük bir belirsizlik kalıyor — insanın da durumu bu.
 */
function isTopRemaining(card: CardId, view: PlayerView): boolean {
  const suit = suitOf(card);
  const seen = new Set<CardId>([...view.hand, ...view.played, ...view.buried]);
  for (const rank of RANKS) {
    if (rank <= rankOf(card)) continue;
    if (!seen.has(cardOf(rank as Rank, suit))) return false;
  }
  return true;
}

/* ------------------------------------------------------------------ *
 * Kart oynama
 * ------------------------------------------------------------------ */

export function chooseCard(view: PlayerView, difficulty: Difficulty = 'normal'): CardId {
  const legal = view.legalCards;
  if (legal.length === 0) throw new Error('Bot için geçerli kart yok — sıra bu koltukta değil');
  if (legal.length === 1) return legal[0]!;
  // Kolay seviye: her zaman en düşük geçerli kart.
  if (difficulty === 'easy') return byRankAsc(legal)[0]!;

  const trump = view.trump!;
  const plays = view.trick.plays;

  if (plays.length === 0) return lead(view, legal, trump);

  const winners = legal.filter((c) => wouldWin(c, plays, trump));
  if (winners.length > 0) {
    /**
     * En UCUZ kazananı seç. Kozla kesmek yerine aynı renkten geçebiliyorsan
     * geç: koz elde dururken her turda yeniden işe yarıyor, aynı renkten bir
     * kart ise yalnızca o turda.
     */
    const offTrump = winners.filter((c) => suitOf(c) !== trump);
    return byRankAsc(offTrump.length > 0 ? offTrump : winners)[0]!;
  }

  return discard(view, legal, trump);
}

/**
 * Lider konumu. Sıralama:
 *   1. Kozdan olmayan kesin alıcı — bedava el, kozu da harcamıyor.
 *   2. Kozu çok ve elinde kozun en büyüğü varsa koz çek: rakiplerin kozu
 *      bitince kalan renk kartları da alıcıya dönüşüyor.
 *   3. Yoksa en kısa yan rengin en düşüğünü at — o rengi boşaltıp sonraki
 *      turlarda kozla kesme hakkı kazan.
 */
function lead(view: PlayerView, legal: readonly CardId[], trump: Suit): CardId {
  const sure = legal.filter((c) => isTopRemaining(c, view));

  const sureOff = sure.filter((c) => suitOf(c) !== trump);
  if (sureOff.length > 0) {
    return sureOff.reduce((a, b) => (rankOf(a) >= rankOf(b) ? a : b));
  }

  const myTrumps = cardsOfSuit(view.hand, trump);
  const sureTrump = sure.filter((c) => suitOf(c) === trump);
  if (sureTrump.length > 0 && myTrumps.length >= 4) {
    return sureTrump.reduce((a, b) => (rankOf(a) >= rankOf(b) ? a : b));
  }

  const groups = groupBySuit(view.hand);
  let best: { card: CardId; length: number } | null = null;
  for (const [suit, cards] of groups) {
    if (suit === trump || cards.length === 0) continue;
    if (best === null || cards.length < best.length) {
      best = { card: cards[0]!, length: cards.length };
    }
  }
  if (best) return best.card;

  return byRankAsc(legal)[0]!;
}

/** Turu alamıyoruz: en ucuz kartı bırak, işe yarayacak kartı sakla. */
function discard(view: PlayerView, legal: readonly CardId[], trump: Suit): CardId {
  const offTrump = legal.filter((c) => suitOf(c) !== trump);
  const pool = offTrump.length > 0 ? offTrump : legal;
  // Kendi renginin en büyüğü olan kart ileride bedava el demek — onu atma.
  const junk = pool.filter((c) => !isTopRemaining(c, view));
  return byRankAsc(junk.length > 0 ? junk : pool)[0]!;
}

/* ------------------------------------------------------------------ *
 * Koz seçimi
 * ------------------------------------------------------------------ */

/**
 * Uzunluk + yükseklik. Uzunluk ağır basıyor: 8'cinin 16 turun yarısını alması
 * gerekiyor ve bunu tek tek yüksek kartlarla değil, rakibin bitirdiği bir
 * renkte kesmeye devam ederek yapıyor.
 */
export function chooseTrump(view: PlayerView): Suit {
  let best: Suit = SUITS[0]!;
  let bestScore = -Infinity;

  for (const suit of SUITS) {
    const cards = cardsOfSuit(view.hand, suit);
    const score =
      cards.length * 3 + cards.reduce((sum, c) => sum + Math.max(0, rankOf(c) - 10), 0);
    if (score > bestScore) {
      bestScore = score;
      best = suit;
    }
  }
  return best;
}

/* ------------------------------------------------------------------ *
 * Gömme
 * ------------------------------------------------------------------ */

/**
 * 8'cinin gömmesi. Yerdeki 4 kart HENÜZ GÖRÜLMEDİĞİ için bu bir bahis:
 * elden çıkan kart kesin, gelen kart meçhul.
 *
 * Öncelik kısa yan renkleri tamamen boşaltmak — boşalan renk, o renk her
 * açıldığında kesme hakkı demek. As barındıran renk boşaltılmıyor: as zaten
 * bedava bir el.
 */
export function chooseBury(view: PlayerView): CardId[] {
  const trump = view.trump!;
  const hand = view.hand;
  const chosen: CardId[] = [];
  const taken = new Set<CardId>();

  const offTrump = hand.filter((c) => suitOf(c) !== trump);
  const groups = [...groupBySuit(offTrump)].sort((a, b) => a[1].length - b[1].length);

  for (const [, cards] of groups) {
    if (chosen.length + cards.length > KITTY_SIZE) continue;
    if (cards.some((c) => rankOf(c) === 14)) continue;
    for (const c of cards) {
      chosen.push(c);
      taken.add(c);
    }
  }

  // Kalanı en düşüklerden tamamla; önce yan renkler, gerekirse koz.
  for (const card of byRankAsc([...offTrump, ...cardsOfSuit(hand, trump)])) {
    if (chosen.length >= KITTY_SIZE) break;
    if (taken.has(card)) continue;
    chosen.push(card);
    taken.add(card);
  }

  return chosen.slice(0, KITTY_SIZE);
}

/* ------------------------------------------------------------------ *
 * Kart alışverişi
 * ------------------------------------------------------------------ */

/**
 * İsteyen taraf olarak ne vereceğimiz.
 *
 * Karşılık zorunlu: verdiğimiz kartın renginden en yükseğini alıyoruz — ama
 * yalnızca elimizdekinden büyükse. Yani iyi bir seçim iki şartı tutuyor:
 *   • verdiğimiz kart düşük olsun (kaybımız az),
 *   • o renkte asa sahip OLMAYALIM (asımız varsa gelecek en iyi kart papaz).
 *
 * O renkten karşı tarafta hiç kart yoksa verdiğimiz kart aynen geri geliyor;
 * o yüzden dışarıda çok kartı kalmış renkler tercih ediliyor.
 */
export function chooseGive(view: PlayerView): { to: Seat; cards: CardId[] }[] {
  const mustGive = view.exchange?.mustGive;
  if (!mustGive) throw new Error('Bottan kart beklenmiyor');

  const total = mustGive.reduce((sum, g) => sum + g.count, 0);
  const seen = new Set<CardId>([...view.hand, ...view.buried]);

  const score = (card: CardId): number => {
    const suit = suitOf(card);
    const mine = cardsOfSuit(view.hand, suit);
    const hasAce = mine.some((c) => rankOf(c) === 14);
    // Dışarıda kalan kart sayısı: karşı tarafta o renkten bulunma şansı.
    const outside = RANKS.filter((r) => !seen.has(cardOf(r as Rank, suit))).length;
    return -rankOf(card) * 3 + outside - (hasAce ? 18 : 0);
  };

  const ranked = [...view.hand].sort((a, b) => score(b) - score(a)).slice(0, total);

  let cursor = 0;
  return mustGive.map((g) => ({
    to: g.to,
    cards: ranked.slice(cursor, (cursor += g.count)),
  }));
}
